/**
 * LegalProSpotlight
 * ─────────────────
 * Hero section that positions NEURACOM Legal Pro as a vertical product
 * for Italian law firms / commercialisti / DPO. Highlights:
 *   - 103 curated Cassazione + CGUE rulings (Jurisprudence DB)
 *   - Private Legal Knowledge Base (save, reuse, ambient context)
 *   - Auto-cite from the user's own KB + jurisprudence as they type
 *   - Claude Opus 4.8 + Fable 5 (1M context) reserved for Legal Pro
 *
 * Visually distinct from the rest of the landing (deep navy + gold accent)
 * to stand out from the generic SaaS sections.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useI18n } from "@/contexts/I18nContext";
import {
  Scale,
  BookMarked,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Zap,
  Quote,
} from "lucide-react";

const KILLER_FEATURES = [
  { key: "jurisprudence", icon: Scale, statKey: "stat_rulings", stat: "103" },
  { key: "knowledge_base", icon: BookMarked, statKey: "stat_kb", stat: "∞" },
  { key: "auto_cite", icon: Sparkles, statKey: "stat_models", stat: "Opus 4.8" },
  { key: "gdpr", icon: ShieldCheck, statKey: "stat_gdpr", stat: "GDPR" },
];

export function LegalProSpotlight() {
  const { t } = useI18n();

  return (
    <section
      id="legal-pro"
      className="relative overflow-hidden bg-gradient-to-br from-[#05073B] via-[#0A0E55] to-[#06082E] py-24 lg:py-32"
      data-testid="legal-pro-spotlight"
    >
      {/* Subtle gold glow corners */}
      <div className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-[#D4A937]/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 h-[600px] w-[600px] rounded-full bg-[#D4A937]/8 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        {/* Header row */}
        <div className="mx-auto max-w-3xl text-center">
          <span
            className="inline-flex items-center gap-2 rounded-full border border-[#D4A937]/40 bg-[#D4A937]/10 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.25em] text-[#F4C955]"
            data-testid="legal-pro-pretitle"
          >
            <Scale className="h-3.5 w-3.5" />
            {t("landing.legal_pretitle")}
          </span>
          <h2
            className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            data-testid="legal-pro-title"
          >
            {t("landing.legal_title")}
          </h2>
          <p
            className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-300"
            data-testid="legal-pro-subtitle"
          >
            {t("landing.legal_subtitle")}
          </p>
        </div>

        {/* Killer features grid */}
        <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {KILLER_FEATURES.map(({ key, icon: Icon, statKey, stat }) => (
            <Card
              key={key}
              className="group relative overflow-hidden border-[#D4A937]/15 bg-white/5 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:border-[#D4A937]/40 hover:bg-white/8"
              data-testid={`legal-pro-feat-${key}`}
            >
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[#D4A937]/15 text-[#F4C955]">
                <Icon className="h-5 w-5" />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#D4A937]">
                {t(`landing.${statKey}`)} · {stat}
              </p>
              <h3 className="mt-2 font-display text-lg font-bold text-white">
                {t(`landing.legal_feat_${key}_title`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">
                {t(`landing.legal_feat_${key}_desc`)}
              </p>
            </Card>
          ))}
        </div>

        {/* "How it works" quick steps */}
        <div className="mt-20 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#9ACD32]">
              <Zap className="h-3 w-3" />
              {t("landing.legal_workflow_pretitle")}
            </div>
            <h3 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white">
              {t("landing.legal_workflow_title")}
            </h3>
            <ol className="mt-8 space-y-5">
              {[1, 2, 3, 4].map((n) => (
                <li
                  key={n}
                  className="flex items-start gap-4"
                  data-testid={`legal-pro-step-${n}`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#D4A937] font-display text-base font-extrabold text-[#05073B]">
                    {n}
                  </span>
                  <div>
                    <p className="font-semibold text-white">
                      {t(`landing.legal_step_${n}_title`)}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-400">
                      {t(`landing.legal_step_${n}_desc`)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Testimonial / Use case card */}
          <div className="relative" data-testid="legal-pro-usecase">
            <Card className="border-[#D4A937]/25 bg-gradient-to-br from-[#0B0E5C]/80 to-[#05073B]/80 p-8 backdrop-blur shadow-[0_20px_60px_rgba(212,169,55,0.15)]">
              <Quote className="h-7 w-7 text-[#D4A937]" />
              <p
                className="mt-4 font-display text-xl leading-snug text-white sm:text-2xl"
                data-testid="legal-pro-quote"
              >
                {t("landing.legal_quote")}
              </p>
              <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#D4A937] font-display text-base font-extrabold text-[#05073B]">
                  AV
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{t("landing.legal_quote_author")}</p>
                  <p className="text-xs text-slate-400">{t("landing.legal_quote_role")}</p>
                </div>
              </div>
            </Card>
            {/* Use cases pills under */}
            <div className="mt-6 flex flex-wrap gap-2">
              {["contracts", "litigation", "gdpr", "labor", "tax", "real_estate"].map((u) => (
                <span
                  key={u}
                  className="rounded-full border border-[#D4A937]/25 bg-[#D4A937]/8 px-3 py-1 text-[11px] font-medium text-slate-200"
                >
                  {t(`landing.legal_use_${u}`)}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 flex flex-col items-center text-center">
          <div className="inline-flex items-baseline gap-3" data-testid="legal-pro-price">
            <span className="font-display text-5xl font-extrabold text-white sm:text-6xl">
              €149
            </span>
            <span className="text-base text-slate-400">{t("landing.legal_price_unit")}</span>
          </div>
          <p className="mt-3 text-sm text-slate-500">{t("landing.legal_price_note")}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-[#D4A937] px-9 py-6 text-base font-bold text-[#05073B] shadow-[0_8px_32px_rgba(212,169,55,0.4)] hover:bg-[#E0B850]"
              data-testid="legal-pro-cta-primary"
            >
              <Link to="/auth?mode=register&plan=legal_pro">
                {t("landing.legal_cta_primary")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-white/30 px-7 py-6 text-base font-semibold text-white hover:bg-white/10 hover:text-white"
              data-testid="legal-pro-cta-secondary"
            >
              <Link to="/pricing#legal">{t("landing.legal_cta_secondary")}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
